import { FC, useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { BordSwitch } from 'bord-design-system';
import NudosDatePicker from '../../../../../../components/NudosComponents/NudosDatePicker/NudosDatePicker';
import { IBasicProductsCare, IProductsCare } from '../../../../../../types/bussines';
import { InternalIcon, ExternalIcon } from 'assets/images/bussines';
import {
  convertSimpleDateToDateTimeZulu,
  formatShortDate,
  getTodayDateForPicker
} from '../../../../../../utils/formatDatesAndHours';
import { REFERENCE_MODEL_TYPE } from '../../../../../../types/global';
import './NudosCareCard.scss';

const CARE_PLAN_FREE = 1;
const CARE_PLAN_FULL = 4;
const DEFAULT_TIMEZONE_OFFSET = -6;

interface INudosCareCardLogistics {
  dataCareCard: IProductsCare;
  isEditable: boolean;
  handleEditProductsCare: (e: IBasicProductsCare) => void;
}

/**
 * Indica si el plan de care corresponde a una suscripción de pago (Full, Basic o Log).
 */
const isPaidCarePlan = (careLevel?: IProductsCare['nudosCare']) =>
  careLevel === 'Full' || careLevel === 'Basic' || careLevel === 'Log';

/**
 * Genera las fechas por defecto al activar Care Full: hoy como inicio y hoy + 1 año como fin.
 */
const getDefaultCareDates = () => {
  const todayPickerValue = getTodayDateForPicker(DEFAULT_TIMEZONE_OFFSET);
  const endPickerValue = DateTime.fromFormat(todayPickerValue, 'yyyy-LL-dd')
    .plus({ years: 1 })
    .toFormat('yyyy-LL-dd');

  return {
    startDate: convertSimpleDateToDateTimeZulu(todayPickerValue, DEFAULT_TIMEZONE_OFFSET),
    endDate: convertSimpleDateToDateTimeZulu(endPickerValue, DEFAULT_TIMEZONE_OFFSET)
  };
};

/**
 * Tarjeta de Care Full con switch Si/No y edición de vigencia mediante date pickers.
 */
const NudosCareCardLogistics: FC<INudosCareCardLogistics> = ({ dataCareCard, isEditable, handleEditProductsCare }) => {
  const [isFullCare, setIsFullCare] = useState(false);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  useEffect(() => {
    if (!dataCareCard) return;
    setIsFullCare(isPaidCarePlan(dataCareCard.nudosCare));
    setStartDate(dataCareCard.startDate ?? null);
    setEndDate(dataCareCard.endDate ?? null);
  }, [dataCareCard]);

  const isKit = !!dataCareCard?.isKit;
  const isSwitchDisabled = !isEditable || isKit;

  /**
   * Emite los cambios del producto al listado editable del tab.
   */
  const emitCareUpdate = (payload: Omit<IBasicProductsCare, 'productId'>) => {
    handleEditProductsCare({ productId: dataCareCard.productId, ...payload });
  };

  /**
   * Alterna entre Care Free y Care Full, aplicando fechas por defecto al activar Full.
   */
  const handleCareSwitchChange = (nextIsFull: boolean) => {
    setIsFullCare(nextIsFull);

    if (nextIsFull) {
      const hasExistingDates = !!startDate || !!endDate;
      const dates = hasExistingDates ? { startDate, endDate } : getDefaultCareDates();

      if (!hasExistingDates) {
        setStartDate(dates.startDate ?? null);
        setEndDate(dates.endDate ?? null);
      }

      emitCareUpdate({
        nudosCare: CARE_PLAN_FULL,
        startDate: dates.startDate,
        endDate: dates.endDate
      });
      return;
    }

    emitCareUpdate({ nudosCare: CARE_PLAN_FREE });
  };

  /**
   * Normaliza el valor del date picker a datetime ISO en utc-0, siguiendo la convención del proyecto.
   */
  const parsePickerDateValue = (value: string): string | null => {
    if (!value) return null;
    const isSimpleDate = /^\d{4}-\d{2}-\d{2}$/.test(value);
    if (isSimpleDate) {
      const zuluDate = convertSimpleDateToDateTimeZulu(value, DEFAULT_TIMEZONE_OFFSET);
      return zuluDate === 'Invalid DateTime' ? null : zuluDate;
    }
    return value;
  };

  /**
   * Actualiza la fecha de inicio del care cuando el usuario edita el date picker.
   */
  const handleStartDateChange = (value: string) => {
    const parsedStartDate = parsePickerDateValue(value);
    setStartDate(parsedStartDate);
    emitCareUpdate({
      nudosCare: CARE_PLAN_FULL,
      startDate: parsedStartDate,
      endDate
    });
  };

  /**
   * Actualiza la fecha de fin del care; vacío representa contrato lifetime (null).
   */
  const handleEndDateChange = (value: string) => {
    const parsedEndDate = parsePickerDateValue(value);
    setEndDate(parsedEndDate);
    emitCareUpdate({
      nudosCare: CARE_PLAN_FULL,
      startDate,
      endDate: parsedEndDate
    });
  };

  const formattedStartDate = startDate
    ? formatShortDate(startDate, DEFAULT_TIMEZONE_OFFSET)?.textWithTimezone
    : null;
  const formattedEndDate = endDate ? formatShortDate(endDate, DEFAULT_TIMEZONE_OFFSET)?.textWithTimezone : null;
  const showDateEditors = isEditable && isFullCare && !isKit;

  return (
    <div
      id="nudosCareCard"
      className={`nudosCareCardLogistics${isEditable && !isKit ? ' nudosCareCardEditing' : ''}`}
    >
      <div className="pointName">
        <div className="blackPoint" />
      </div>
      <div className="toolName">
        {`${dataCareCard?.serial ? `${dataCareCard?.serial} /` : ''} ${dataCareCard.nudosId} - ${
          dataCareCard.productName
        }`}
        <div className="containerTypeTool">
          {dataCareCard.referenceCatalog === REFERENCE_MODEL_TYPE.INTERNAL && <InternalIcon />}
          {dataCareCard.referenceCatalog === REFERENCE_MODEL_TYPE.EXTERNAL && <ExternalIcon />}
          {dataCareCard.referenceCatalog === REFERENCE_MODEL_TYPE.STOCK && <InternalIcon />}
        </div>
      </div>
      <div className="careSwitchContainer">
        <BordSwitch
          checked={isFullCare}
          offLabel="No"
          onLabel="Si"
          disabled={isSwitchDisabled}
          onChange={handleCareSwitchChange}
        />
      </div>
      <div className="dateCareContainer">
        {showDateEditors ? (
          <div className="dateCareEditorContainer">
            <div className="dateCareEditorField">
              <span className="dateCareEditorLabel">Inicio:</span>
              <NudosDatePicker
                customClassName="bordDatePickerDS"
                isFilled={!!startDate}
                defaultValue={startDate ?? undefined}
                desiredTimeZone={DEFAULT_TIMEZONE_OFFSET}
                updateCurrentValueCallback={handleStartDateChange}
              />
            </div>
            <div className="dateCareEditorField">
              <span className="dateCareEditorLabel">Fin:</span>
              <NudosDatePicker
                customClassName="bordDatePickerDS"
                isFilled={!!endDate}
                defaultValue={endDate ?? undefined}
                desiredTimeZone={DEFAULT_TIMEZONE_OFFSET}
                updateCurrentValueCallback={handleEndDateChange}
              />
            </div>
          </div>
        ) : (
          <div className="dateCareDisplayRow">
            <div className="startCareDate">
              {formattedStartDate ? (
                <div className="startDate">{`Inicio: ${formattedStartDate}`}</div>
              ) : (
                <div className="noStartDate">Inicio: -</div>
              )}
            </div>
            <div className="endCareDate">
              {isFullCare && !endDate ? (
                <div className="endDate">Fin: Lifetime</div>
              ) : formattedEndDate ? (
                <div className="endDate">{`Fin: ${formattedEndDate}`}</div>
              ) : (
                <div className="noEndDate">Fin: -</div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="grayLine" />
    </div>
  );
};

export default NudosCareCardLogistics;
